import { useCallback, useEffect, useRef, useState } from "react";
import { ListChecks, Plus } from "lucide-react";
import { ApiError } from "../lib/api";
import type { ChecklistCommand, ContractWork, ContractChecklistItem } from "../lib/api-client/contracts";
import { Checkbox } from "./beui/Checkbox";
import { Input } from "./beui/Input";
import { Button } from "./beui/Button";
import { NumberTicker } from "./beui/NumberTicker";
import { Alert, AlertIcon, AlertDescription } from "./beui/Alert";

const time = (value: string) => new Date(value).toLocaleString("en-PH", { timeZone: "Asia/Manila", dateStyle: "medium", timeStyle: "short" });
const accessLost = (error: unknown) => error instanceof ApiError && (error.status === 401 || error.status === 404
  || error.status === 403 && !["legal_reacceptance_required", "company_verification_required"].includes(error.code || ""));

type Props = {
  loadWork: () => Promise<ContractWork>;
  saveCommand: (command: ChecklistCommand) => Promise<{ item: ContractChecklistItem; replayed: boolean }>;
  writable: boolean; refreshKey: string; minimumLabel: number; maximumLabel: number;
  roleLabels: Record<string, string>; onAccessLost?: () => void;
};

/** Contract and Job adapters own admission, lifecycle and label limits; this shared presentation owns recovery.
 * Record/profile keys at callers discard drafts and uncertain commands when the selected work changes. */
export function WorkChecklist({ loadWork, saveCommand, writable, refreshKey, minimumLabel, maximumLabel, roleLabels, onAccessLost }: Props) {
  const [work, setWork] = useState<ContractWork | null>(null);
  const [loading, setLoading] = useState(true);
  const [readError, setReadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const active = useRef(false);
  // Read versions choose the latest snapshot; epochs invalidate the view, and write versions own busy state.
  // Keep the pending command through lifecycle refresh so an uncertain save can still replay with fresh admission.
  const readVersion = useRef(0);
  const accessEpoch = useRef(0);
  const writeVersion = useRef(0);
  const pending = useRef<ChecklistCommand | null>(null);
  const saving = useRef(false);

  const clearAccess = useCallback(() => {
    readVersion.current++; accessEpoch.current++; writeVersion.current++;
    saving.current = false; pending.current = null;
    setWork(null); setLabel(""); setDenied(true); setBusy(false); setLoading(false); setMessage(null); setSaveError(null);
    onAccessLost?.();
  }, [onAccessLost]);
  const load = useCallback(async (saved = false) => {
    const version = ++readVersion.current;
    setLoading(true); setReadError(null);
    try {
      const result = await loadWork();
      if (!active.current || version !== readVersion.current) return;
      setWork(result); setDenied(false);
    } catch (error) {
      if (!active.current || version !== readVersion.current) return;
      if (accessLost(error)) clearAccess();
      setReadError(saved ? "Saved. Could not refresh the checklist. Retry to load the latest history."
        : error instanceof Error ? error.message : "Could not load the checklist.");
    } finally {
      if (active.current && version === readVersion.current) setLoading(false);
    }
  }, [loadWork, clearAccess]);
  useEffect(() => {
    active.current = true; saving.current = false; setBusy(false);
    void load();
    return () => { active.current = false; readVersion.current++; accessEpoch.current++; writeVersion.current++; };
  }, [load, refreshKey]);

  const canWrite = !!work?.canWrite && writable && !denied && !readError;
  async function save(command: ChecklistCommand) {
    if (saving.current || denied || readError || !work || !canWrite && !pending.current) return;
    const epoch = accessEpoch.current;
    const version = ++writeVersion.current;
    saving.current = true; setBusy(true); setSaveError(null); setMessage(null);
    // Retain this exact command across an uncertain network/commit outcome.
    pending.current ??= command;
    const sent = pending.current;
    try {
      const result = await saveCommand(sent);
      if (!active.current || epoch !== accessEpoch.current || version !== writeVersion.current) return;
      pending.current = null;
      setWork(current => current && ({ ...current, items: current.items.some(item => item.id === result.item.id)
        ? current.items.map(item => item.id === result.item.id ? result.item : item) : [...current.items, result.item] }));
      if (sent.action === "add") setLabel("");
      setMessage("Saved.");
      await load(true);
    } catch (error) {
      if (!active.current || epoch !== accessEpoch.current || version !== writeVersion.current) return;
      if (accessLost(error)) clearAccess();
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        pending.current = null;
        if (error.code === "work_read_only" || error.code === "company_verification_required") void load();
      }
      setSaveError(error instanceof ApiError && error.code === "work_read_only" ? "Not saved. Work is read-only."
        : error instanceof Error ? error.message : "Could not confirm the save. Retry the same action.");
    } finally {
      if (version === writeVersion.current) {
        saving.current = false;
        if (active.current) setBusy(false);
      }
    }
  }
  const done = work?.items.filter(item => item.done).length ?? 0;
  return <section aria-label="Checklist" className="wu-checklist min-w-0 max-w-full">
    <div className="wu-section-heading"><h2><ListChecks size={18} aria-hidden="true" />Checklist</h2>
      {work && <NumberTicker value={done} suffix={`/${work.items.length} done`} startOnView={false} duration={.35} />}
    </div>
    {loading && <p role="status" className="text-sm text-mut">Loading checklist…</p>}
    {readError && <div role="alert" className="mb-3 text-sm text-brand-red">{readError} <button type="button" className="underline" onClick={() => void load()}>Retry</button></div>}
    {work && <>
      {work.readOnlyReason && <p className="mb-3 text-sm text-mut">{work.readOnlyReason}</p>}
      {!loading && !readError && work.items.length === 0 && <p className="mb-3 text-sm text-mut">No checklist items yet.</p>}
      <ul className="wu-checklist-items">{work.items.map(item => <li key={item.id}>
        <Checkbox checked={item.done} onCheckedChange={value => void save({ commandId: crypto.randomUUID(), action: "set", itemId: item.id, done: value })}
          disabled={!canWrite || busy || !!pending.current} aria-label={item.label} label={item.label}
          className={`min-h-7 min-w-0 w-full [overflow-wrap:anywhere] ${item.done ? "text-mut line-through" : "text-navy"}`} />
      </li>)}</ul>
    </>}
    <form className="wu-add-task" onSubmit={event => {
      event.preventDefault();
      if (pending.current) void save(pending.current);
      else if (label.trim().length >= minimumLabel) void save({ commandId: crypto.randomUUID(), action: "add", label: label.trim() });
    }}>
      <Input aria-label="New checklist item" classNames={{ field: "h-[42px] rounded-full" }} placeholder="Add an item" value={label} maxLength={maximumLabel}
        onChange={setLabel} disabled={(!canWrite && !pending.current) || denied || !!readError || busy} readOnly={!!pending.current} />
      <Button type="submit" variant="outline" size="icon" aria-label={busy ? "Saving checklist item" : pending.current ? "Retry save" : "Add checklist item"} className={pending.current ? "wu-retry" : ""} disabled={denied || !!readError || !work || busy || !pending.current && (!canWrite || label.trim().length < minimumLabel)}>{pending.current ? "Retry save" : <Plus size={18} aria-hidden="true" />}</Button>
    </form>
    <Alert open={!!saveError || !!message} variant={saveError ? "destructive" : "success"} className="wu-feedback"><AlertIcon /><AlertDescription>{saveError ? <>{saveError}{pending.current && " Retry to recover the same action."}</> : message}</AlertDescription></Alert>
    {work && <details className="wu-history"><summary className="cursor-pointer py-2 font-semibold text-navy">Checklist history</summary>
      <p className="my-2 text-xs text-mut">Action history starts with the shared checklist. Earlier authors and actions may be unknown.</p>
      {work.history.length === 0 && <p className="text-sm text-mut">No recorded actions yet.</p>}
      <ol className="space-y-3">{work.history.map(event => <li key={event.id} className="text-sm text-navy">
        <p className="break-words"><span className="font-semibold">{event.actorName || "Unknown author"}</span> {event.action === "add" ? "added" : event.action === "done" ? "marked done" : "marked undone"}: {event.label}</p>
        <p className="mt-1 text-xs text-mut">{roleLabels[event.actorRole] || "Unknown role"}{event.actorCompanyId ? " · Business profile" : " · Personal"} · <time dateTime={event.createdAt}>{time(event.createdAt)} PHT</time></p>
      </li>)}</ol>
    </details>}
  </section>;
}
